import { useCallback, useEffect, useRef, useState } from 'react';
import { SystemLogService } from '../services/SystemLogService';
import type { PaginationInfo } from '../types';
import type { SystemLog, SystemLogFilters } from '../types/systemLog';

const SEARCH_DEBOUNCE_MS = 400;

export interface UseSystemLogsReturn {
  rows: SystemLog[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return 'Erro ao carregar logs';
};

export function useSystemLogs(
  page: number,
  pageSize: number = 20,
  filters: SystemLogFilters = {},
): UseSystemLogsReturn {
  const [rows, setRows] = useState<SystemLog[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const previousFiltersRef = useRef(filters);

  const fetchPage = useCallback(
    async (targetPage: number, targetPageSize: number = pageSize, serverFilters: SystemLogFilters = {}) => {
      const currentRequestId = ++requestIdRef.current;

      try {
        setLoading(true);
        setError(null);

        const response = await SystemLogService.listPaginated(targetPage, targetPageSize, serverFilters);

        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setRows(response.data);
        setPagination(response.pagination);
      } catch (requestError) {
        if (currentRequestId !== requestIdRef.current) {
          return;
        }

        setError(getErrorMessage(requestError));
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    },
    [pageSize],
  );

  useEffect(() => {
    const onlySearchChanged =
      filters.logLevel === previousFiltersRef.current.logLevel &&
      filters.logSystem === previousFiltersRef.current.logSystem &&
      filters.dateFrom === previousFiltersRef.current.dateFrom &&
      filters.dateTo === previousFiltersRef.current.dateTo &&
      filters.search !== previousFiltersRef.current.search;

    previousFiltersRef.current = filters;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, filters);
      }, SEARCH_DEBOUNCE_MS);

      return () => clearTimeout(debounceTimer.current);
    }

    clearTimeout(debounceTimer.current);
    fetchPage(page, pageSize, filters);

    return () => clearTimeout(debounceTimer.current);
  }, [fetchPage, filters, page, pageSize]);

  const refetch = useCallback(() => {
    fetchPage(page, pageSize, filters);
  }, [fetchPage, filters, page, pageSize]);

  return {
    rows,
    pagination,
    loading,
    error,
    refetch,
  };
}